import { keyBy } from 'lodash-es'
import { treq } from '#app/lib/treq'
import { truncateBboxDecimals } from '#map/lib/geo'
import type { ItemsPage } from '#server/controllers/items/lib/queries_commons'
import type { PageParams } from '#server/lib/pagination'
import type { BBox } from '#server/types/common'
import type { EntityUri } from '#server/types/entity'
import type { Item, ItemId } from '#server/types/item'
import type { UserId } from '#server/types/user'
import { getRelations } from '#users/lib/relations'
import { serializeUser, type SerializedUser } from '#users/lib/users'
import { serializeItem, setItemUserData, type SerializedItemWithUserData } from './items'
import type { OverrideProperties } from 'type-fest'
import type { WikimediaLanguageCode } from 'wikibase-sdk'

export type SerializedItemsPage = OverrideProperties<ItemsPage, { items: SerializedItemWithUserData[], users: SerializedUser[] }>

export async function getItemsByUserIdAndEntities (userId: UserId, uris: EntityUri[]) {
  const { items, users } = await treq('items', 'get', 'by-user-and-entities', { user: userId, uris, 'with-users': true })
  const serializedUsers = users.map(serializeUser)
  const serializedItems = items.map(serializeItem)
  const usersByIds = keyBy(serializedUsers, '_id')
  return serializedItems.map(item => {
    return setItemUserData(item, usersByIds[item.owner])
  }) as SerializedItemWithUserData[]
}

export async function getItemsByIds ({ ids, items }) {
  const res = await treq('items', 'get', 'by-ids', { ids, 'with-users': true })
  updateItemsParams(res, { items })
  return res as SerializedItemsPage
}

export async function getSerializedItemWithUser (itemId: ItemId) {
  const res = await treq('items', 'get', 'by-ids', { ids: [ itemId ], 'with-users': true })
  serializeItemsWithUsers(res)
  return res.items[0] as SerializedItemWithUserData
}

export async function getNearbyItems (params: ItemsPageParams) {
  const { limit, offset } = params
  const res = await treq('items', 'get', 'nearby', { limit, offset })
  return updateItemsParams(res, params)
}

export async function getRecentPublicItems (params: ItemsPageParams & { lang: WikimediaLanguageCode, assertImage: boolean }) {
  const { limit, lang, assertImage } = params
  const res = await treq('items', 'get', 'recent-public', { limit, lang, 'assert-image': assertImage })
  return updateItemsParams(res, params)
}

export async function getItemsByBbox (params: ItemsPageParams & { bbox: BBox, lang: WikimediaLanguageCode }) {
  const { bbox, limit, lang } = params
  const res = await treq('items', 'get', 'by-bbox', { bbox: truncateBboxDecimals(bbox), limit, lang })
  return updateItemsParams(res, params)
}

export async function getUserItems (params: ItemsPageParams & { userId: UserId }) {
  const { userId } = params
  return makeRequestAlt(params, [ userId ])
}

export async function getNetworkItems (params: ItemsPageParams) {
  const { network: networkIds } = await getRelations()
  return makeRequestAlt(params, networkIds)
}

async function makeRequestAlt (params: ItemsPageParams, usersIds: UserId[]) {
  if (usersIds.length === 0) return { items: [] as Item[], total: 0 } as ItemsPage
  const { limit, offset } = params
  const res = await treq('items', 'get', 'by-users', { users: usersIds, limit, offset, 'with-users': true })
  return updateItemsParams(res, params)
}

interface ItemsPageParams extends Partial<PageParams> {
  hasMore?: boolean
  items: SerializedItemWithUserData[]
}

function updateItemsParams (res: ItemsPage, params: ItemsPageParams) {
  const { items: newItems, continue: continu, total } = res
  params.hasMore = continu != null
  params.total = total
  serializeItemsWithUsers(res)
  params.items.push(...(newItems as SerializedItemWithUserData[]))
  return res as SerializedItemsPage
}

export function serializeItemsWithUsers (res: ItemsPage) {
  const { items, users } = res
  const usersById = keyBy(users.map(serializeUser), '_id')
  res.items = items.map(serializeItem)
  items.forEach(item => {
    // @ts-expect-error
    item.user = usersById[item.owner]
  })
  return res as SerializedItemsPage
}

export type ItemsPageRequestFn = typeof getNetworkItems | typeof getNearbyItems
